import * as React from "react";
import { createStyles, makeStyles } from "@material-ui/styles";
import {
    FeedbackModal,
    FeedbackSearchIcons,
    FeedbackTypeEnum,
    Input,
    ISearchFacetConfig,
    MessageComposerIconButton,
    Page,
    PageHeaderActions,
    PageSection,
    Paginator,
    RelodeTheme,
    SearchContainer,
    TableCellRoute,
    TableColumn,
    useNotifications,
    useRouter,
} from "@relode/core";

import { candidates } from "../../../shared/backend/controllers";
import { useSearch } from "../_infra/search/candidateSearchProvider";

const useStyles = makeStyles(
    (theme: RelodeTheme) =>
        createStyles({
            keywordInput: {
                margin: theme.spacing(1, 0, 2, 0),
            },
            searchButton: {
                padding: 0,
            },
            searchButtonIcon: {
                fontSize: "1rem",
            },
            daysInStatusColumn: {
                width: "12%",
            },
        }),
    { name: "CandidateSearch" }
);

const NOT_SHARED_JOB_MESSAGE =
    "You are not shared to this job. You must be shared to this job to give feedback.";
const JOB_NOT_CORRECT_STATUS_MESSAGE =
    "Feedback can only be given when a candidate is in Submitted or Interviewing status.";

export const CandidateSearchDesktop = () => {
    const classes = useStyles({});
    const [searchState, searchActions] = useSearch();
    const { actions: notifications } = useNotifications();
    const { location } = useRouter();

    const [searchText, setSearchText] = React.useState("");
    const [backUrl, setBackUrl] = React.useState<string>(undefined);

    const [
        isFeedbackModalOpen,
        setIsFeedbackModalOpen,
    ] = React.useState<boolean>(false);
    const [feedbackModalProps, setFeedbackModalProps] = React.useState<{
        jobName: string;
        candidateFullName: string;
        leadId: number;
        jobId: number;
        comment: string;
        selectedFeedbackType: FeedbackTypeEnum;
        hasGivenFeedback: boolean;
    }>(undefined);

    const { searchResults, searchCriteria } = searchState;

    React.useEffect(() => {
        if (!!location.search) {
            setBackUrl(`/candidates${location.search}`);
        } else {
            setBackUrl("/candidates");
        }
    }, [location.search]);

    React.useEffect(() => {
        setSearchText(searchCriteria.searchText || "");
    }, [searchCriteria]);

    const handleSearch = () => {
        searchActions.searchText(searchText);
    };

    const handleMultipleSelectionFilter = (
        fId: string,
        fName: string,
        itemName?: string
    ) => (catItem?: { label: string; value: string }) => {
        searchActions.filterMultiple(fId, fName, itemName, catItem);
    };

    const handleFilter = (fId: string, fName: string) => (catItem?: {
        label: string;
        value: string;
    }) => {
        searchActions.filterSingle(fId, fName, catItem);
    };

    const getSelectedFeedbackType = (
        vote: boolean,
        hasGivenFeedback: boolean
    ) => {
        if (hasGivenFeedback) {
            return vote ? FeedbackTypeEnum.Positive : FeedbackTypeEnum.Negative;
        }
        return null;
    };

    const openFeedbackModal = (
        selectedFeedbackType: FeedbackTypeEnum,
        jobId: number,
        jobName: string,
        leadId: number,
        candidateFullName: string,
        comment: string,
        canGiveFeedback: boolean,
        hasGivenFeedback: boolean,
        isJobUser: boolean
    ) => {
        if (canGiveFeedback) {
            setFeedbackModalProps({
                jobId,
                leadId,
                candidateFullName,
                jobName,
                comment,
                selectedFeedbackType,
                hasGivenFeedback: hasGivenFeedback,
            });

            setIsFeedbackModalOpen(true);
        } else if (!isJobUser) {
            notifications.showModal(
                "info",
                `Unable to give feedback for ${candidateFullName}`,
                NOT_SHARED_JOB_MESSAGE
            );
        } else {
            notifications.showModal(
                "info",
                `Unable to change feedback for ${candidateFullName}`,
                JOB_NOT_CORRECT_STATUS_MESSAGE
            );
        }
    };

    const addOrUpdateFeedback = async (
        comment: string,
        vote: boolean,
        hasGivenFeedback: boolean,
        jobId: number,
        leadId: number
    ) => {
        if (hasGivenFeedback) {
            await searchActions.updateFeedback(jobId, leadId, vote, comment);
        } else {
            await searchActions.addFeedback(jobId, leadId, vote, comment);
        }
        setIsFeedbackModalOpen(false);
    };

    const facetConfig: ISearchFacetConfig = {
        handleMultipleSelection: handleMultipleSelectionFilter,
        handleSingleSelection: handleFilter,
        facets: [
            {
                id: "candidateStatus",
                title: "candidate status",
                idPropName: "jobCandidateStatusIds",
                namePropName: "jobCandidateStatusNames",
                isMultiple: true,
            },
            {
                id: "location",
                title: "job location",
                idPropName: "locationId",
                namePropName: "locationName",
                isMultiple: false,
            },
            {
                id: "jobTitle",
                title: "job title",
                idPropName: "jobId",
                namePropName: "jobTitleName",
                isMultiple: false,
            },
            {
                id: "jobStatus",
                title: "job status",
                idPropName: "jobStatusIds",
                namePropName: "jobStatusNames",
                isMultiple: true,
            },
            {
                id: "jobType",
                title: "job type",
                idPropName: "jobTypeIds",
                namePropName: "jobTypeNames",
                isMultiple: true,
            },
            {
                id: "team",
                title: "organization",
                idPropName: "teamId",
                namePropName: "TeamName",
                isMultiple: false,
            },
            {
                id: "jobScope",
                title: "job scope",
                idPropName: "jobScope",
                namePropName: "jobScopeName",
                isMultiple: false,
            },
            // These are "fake" facets; they are used only the display the query params values to refined by section;
            // if those filters are removed than these facets will not be displayed since they are not returned by API endpoint
            {
                id: "job",
                title: "job",
                idPropName: "jobId",
                namePropName: "jobName",
                isMultiple: false,
            },
        ],
    };

    const getColumns = (): TableColumn[] => [
        {
            title: "candidate name",
            field: "candidateFullName",
            sortable: true,
            columnSize: "large",
            truncateText: true,
            render: (rowData: candidates.CandidateSearchLeadResponse) => (
                <TableCellRoute
                    title={`${rowData.candidateFullName || "n/a"}`}
                    pathname={`/candidates/${rowData.jobId}/${rowData.leadId}`}
                    backUrl={backUrl}
                >
                    {rowData.candidateFullName || "n/a"}
                </TableCellRoute>
            ),
        },
        {
            title: "job",
            field: "jobName",
            sortable: true,
            columnSize: "large",
            truncateText: true,
            render: (rowData: candidates.CandidateSearchLeadResponse) => (
                <TableCellRoute
                    title={`${rowData.jobName || "n/a"}`}
                    pathname={`/jobs/${rowData.jobId}`}
                    backUrl={backUrl}
                >
                    {rowData.jobName || "n/a"}
                </TableCellRoute>
            ),
        },
        {
            title: "location",
            field: "locationName",
            sortable: true,
            columnSize: "medium",
            truncateText: true,
        },
        {
            title: "status",
            field: "candidateStatusName",
            sortable: true,
            columnSize: "small",
        },
        {
            title: "days in status",
            field: "daysInStatus",
            sortable: true,
            columnSize: "small",
            className: classes.daysInStatusColumn,
        },
        {
            title: "feedback",
            field: "positiveVoteCount",
            sortable: true,
            columnSize: "medium",
            render: (rowData: candidates.CandidateSearchLeadResponse) => (
                <>
                    <FeedbackSearchIcons
                        onClick={openFeedbackModal}
                        jobId={rowData.jobId}
                        jobName={rowData.jobName}
                        leadId={rowData.leadId}
                        fullName={rowData.candidateFullName}
                        comment={rowData.comment}
                        selectedFeedbackType={getSelectedFeedbackType(
                            rowData.vote,
                            rowData.hasGivenFeedback
                        )}
                        canGiveFeedback={rowData.canAddEditFeedback}
                        hasGivenFeedback={rowData.hasGivenFeedback}
                        showFeedbackCounts={rowData.canViewFeedback}
                        upCount={rowData.positiveVoteCount}
                        downCount={rowData.negativeVoteCount}
                        isJobUser={rowData.isJobUser}
                        feedbackMessage={
                            rowData.canAddEditFeedback
                                ? ""
                                : rowData.isJobUser
                                ? JOB_NOT_CORRECT_STATUS_MESSAGE
                                : NOT_SHARED_JOB_MESSAGE
                        }
                    />
                </>
            ),
        },
        {
            title: "actions",
            field: "actions",
            align: "right",
            columnSize: "small",
            sortable: false,
            render: (rowData: candidates.CandidateSearchLeadResponse) => (
                <MessageComposerIconButton
                    id="sendMessage"
                    leadId={rowData.leadId}
                    leadName={rowData.candidateFullName}
                    jobId={rowData.jobId}
                    jobName={rowData.jobName}
                />
            ),
        },
    ];

    const handleTextSearchChange = (event: React.ChangeEvent<any>) => {
        setSearchText(event.target.value);
    };

    const handleTextSearchKeyPress = (event: React.KeyboardEvent) => {
        if (event.key === "Enter") {
            searchActions.searchText(searchText);
        }
    };

    const handleRequestSort = (
        event: React.MouseEvent<HTMLElement, MouseEvent>,
        property: string
    ) => {
        searchActions.sort(property);
    };

    const renderActionBar = (): React.ReactNode => {
        if (!searchResults) return;
        const { totalResults } = searchResults;
        const { pageSize, pageIndex } = searchCriteria;
        const start = (pageIndex - 1) * pageSize + 1;
        const remaining =
            totalResults < pageSize ? totalResults : totalResults - start;
        const maxOnPage =
            remaining < pageSize ? totalResults : pageIndex * pageSize;

        return (
            <PageHeaderActions
                right={
                    !!searchResults &&
                    totalResults > 0 && (
                        <Paginator
                            baseId="candidatesSearch"
                            isBusy={searchState.searching}
                            total={totalResults}
                            range={{
                                start,
                                end: maxOnPage,
                            }}
                            showAsRange
                            enableTooManyResultsMessage
                            onNavigate={changePage}
                        />
                    )
                }
            />
        );
    };

    const changePage = (direction: "prev" | "next") => {
        searchActions.navigate(direction, searchResults?.totalResults);
    };

    return (
        <>
            <Page
                id="candidatesSearchPage"
                label="candidates"
                isBusy={searchState.searching}
                actions={renderActionBar()}
                busyType="bar"
            >
                <PageSection>
                    <SearchContainer
                        id="candidatesSearch"
                        entityName="candidates"
                        facets={searchResults?.facets}
                        facetConfig={facetConfig}
                        hasResults={searchResults?.totalResults > 0}
                        criteria={searchCriteria}
                        onRequestSort={handleRequestSort}
                        getColumns={getColumns}
                        sortDir={searchCriteria.sortDir}
                        sortField={searchCriteria.sortField}
                        totalCount={searchResults?.totalResults}
                        data={() => searchResults?.candidates}
                        onSearch={handleSearch}
                        searchDisabled={
                            searchText === searchCriteria.searchText
                        }
                        isBusy={searchState.searching}
                    >
                        <div>
                            <Input
                                autoComplete="off"
                                fullWidth
                                className={classes.keywordInput}
                                value={searchText}
                                autoFocus={true}
                                label="name, position, etc"
                                onChange={handleTextSearchChange}
                                onKeyPress={handleTextSearchKeyPress}
                                inputProps={{
                                    id: "candidatesSearchKeywordInput",
                                    maxLength: 150,
                                }}
                                hideCharacterLimit={true}
                            />
                        </div>
                    </SearchContainer>
                </PageSection>
            </Page>
            {isFeedbackModalOpen ? (
                <FeedbackModal
                    onCancel={() => setIsFeedbackModalOpen(false)}
                    onConfirm={addOrUpdateFeedback}
                    isLoading={searchState.searching}
                    {...feedbackModalProps}
                />
            ) : null}
        </>
    );
};
